import React, {
  useRef,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import {
  useTheme,
  Box,
  Avatar,
  AvatarGroup,
  Typography,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Menu,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  Popover,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Checkbox,
  Divider,
  ClickAwayListener,
  InputBase,
  CircularProgress,
} from "@mui/material";
import BoltIcon from "@mui/icons-material/Bolt";
import {
  DataGridPro,
  GridColDef,
  GridPaginationModel,
  GridRowId,
  GridRowParams,
  GridActionsCellItem,
  GridOverlay,
  GridRenderEditCellParams,
  GridRenderCellParams,
  GridRow,
} from "@mui/x-data-grid-pro";
import EditIcon from "@mui/icons-material/Edit";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useTranslation } from "react-i18next";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { Person, Check, Close, Search } from "@mui/icons-material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import ViewListIcon from "@mui/icons-material/ViewList";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ViewAgendaIcon from "@mui/icons-material/ViewAgenda";
import { colorPalette, typeToIconMapping } from "../utils";
import Pagination from "@/Components/Common/TablePro/Pagination";

import { useAppSelector } from "@/app/hooks";
import { selectApiData } from "@/features/bughurd/boardSelector";
import { useGetBugDetailByBugIdQuery } from "@/features/bughurd/api";

// Drag and Drop imports
import { useDroppable, useDraggable } from "@dnd-kit/core";

// Define DraggableHandle component outside to avoid hook violations
const DraggableHandle = ({
  id,
  disabled = false,
}: {
  id: string;
  disabled?: boolean;
}) => {
  const theme = useTheme();
  const { attributes, listeners, setNodeRef } = useDraggable({
    id,
    data: {
      type: "task",
      taskId: id,
    },
    disabled,
  });

  return (
    <Box
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: disabled ? "default" : "grab",
        height: "100%",
        color: !disabled
          ? theme.palette.primary.main
          : theme.palette.text.disabled,
        opacity: !disabled ? 1 : 0.3,
        transition: "opacity 0.2s ease",
        "&:active": {
          cursor: disabled ? "default" : "grabbing",
        },
      }}
    >
      <DragIndicatorIcon fontSize="small" />
    </Box>
  );
};

interface BugItem {
  _id: string;
  bugKey: string;
  summary: string;
  assignTo: Array<{
    _id: string;
    fullName: string;
  }>;
  bugStatus: {
    _id: string;
    statusCode: string;
    status: string;
  };
  severity: string;
  type: string;
  createdBy: {
    _id: string;
    fullName: string;
  };
  project: {
    _id: string;
    name: string;
    code: string;
  };
  subTasksCount?: number;
  createdAt: string;
  updatedAt: string;
  epicTasks?: any[];
  subTasks?: any[];
  epic?: {
    _id: string;
    bugKey: string;
    summary: string;
  };
}

interface TaskDataGridProps {
  accordionKey: string;
  droppableId: string;
  title: string;
  isExpanded: boolean;
  onAccordionChange: (event: React.SyntheticEvent, isExpanded: boolean) => void;
  tasks: BugItem[];
  totalCount: number;
  isLoading: boolean;
  paginationModel: {
    page: number;
    pageSize: number;
  };
  setPaginationModel: React.Dispatch<
    React.SetStateAction<{ page: number; pageSize: number }>
  >;
  onTaskClick: (taskId: string) => void;
  onMenuClick: (event: React.MouseEvent<HTMLElement>, taskId: string) => void;
  isCreatingTask: boolean;
  newTaskName: string;
  onCreateTaskClick: () => void;
  onTaskNameChange: (name: string) => void;
  onCreateTask: () => void;
  onCancelTaskCreation: () => void;
  onKeyPress: (event: React.KeyboardEvent) => void;
  isCreatingBug: boolean;
  headerButtons?: Array<{
    label: string;
    icon: React.ReactNode;
    onClick: (event: React.MouseEvent) => void;
    disabled?: boolean;
  }>;
  sprint?: {
    _id: string;
    name: string;
    status: string;
    startDate?: string;
    endDate?: string;
    sprintKey?: string;
    description?: string;
    totalBugCount?: number;
    doneBugCount?: number;
  };
  onDeleteSprint?: (sprintId: string) => void;
  onEditSprint?: (sprintId: string) => void;
  isDeletingSprint?: boolean;
  emptyMessage?: string;
  isDragging?: boolean;
  selectedTasks: Set<string>;
  onSelectionChange: (selection: Set<string>) => void;
  isSelectionDisabled?: boolean;
  onStatusClick?: (
    event: React.MouseEvent<HTMLElement>,
    taskId: string
  ) => void;
  editingStatusTaskId?: string | null;
  statusList?: any[];
  isUpdatingStatus?: boolean;
  viewMode?: "epics" | "stories" | "all";
  onViewModeChange?: (
    event: React.MouseEvent<HTMLElement>,
    newMode: "epics" | "stories" | "all" | null
  ) => void;
  availableUsers?: any[];
  availableTypes?: any[];
  onTaskUpdate?: (taskId: string, obj: any, field: string) => any;
  newTaskType?: string;
  onNewTaskTypeChange?: (typeCode: string) => void;
}

/**
 * SubTasksDetailPanel Component
 * Shows both epic tasks (linked tasks) AND sub-tasks if available
 */
interface SubTasksDetailPanelProps {
  parentTaskId: string;
  parentTask: BugItem;
  onTaskClick?: any;
}

const SubTasksDetailPanel: React.FC<SubTasksDetailPanelProps> = ({
  parentTaskId,
  parentTask,
  onTaskClick,
}) => {
  const { t } = useTranslation(["bugherd"]);
  const theme = useTheme();

  const hasEpicTasksInRow = (parentTask?.epicTasks?.length ?? 0) > 0;
  const hasSubTasksInRow = (parentTask?.subTasks?.length ?? 0) > 0;
  const needsFetch =
    !hasEpicTasksInRow &&
    !hasSubTasksInRow &&
    (parentTask?.subTasksCount ?? 0) > 0;

  const { data: bugDetailData, isLoading: isLoadingSubTasks } =
    useGetBugDetailByBugIdQuery(
      { bugId: parentTaskId, withDetails: true },
      { skip: !needsFetch || !parentTaskId, refetchOnMountOrArgChange: true }
    );

  const epicTasks = useMemo(() => {
    const tasks = parentTask?.epicTasks || [];
    return tasks.map((task: any) => ({
      id: task._id,
      ...task,
    }));
  }, [parentTask?.epicTasks]);

  const subTasks = useMemo(() => {
    let rawTasks: any[] = [];
    if (hasSubTasksInRow) {
      rawTasks = parentTask?.subTasks || [];
    } else if (bugDetailData) {
      rawTasks = bugDetailData?.data?.decryptedData?.subTasks || [];
    }
    return rawTasks.map((task: any) => ({
      id: task._id,
      ...task,
    }));
  }, [parentTask?.subTasks, hasSubTasksInRow, bugDetailData]);

  const hasEpicTasks = epicTasks.length > 0;
  const hasSubTasks = hasEpicTasks ? false : subTasks.length > 0;

  const renderAssignee = useCallback(
    (assignees: any[]) => {
      if (assignees && assignees.length > 0) {
        return (
          <AvatarGroup
            max={2}
            spacing="medium"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              height: "100%",
              "& .MuiAvatar-root": {
                width: 20,
                height: 20,
                fontSize: 10,
              },
            }}
          >
            {assignees.map((user, index) => (
              <Tooltip key={user._id} title={user.fullName}>
                <Avatar
                  sx={{ bgcolor: colorPalette[index % colorPalette.length] }}
                >
                  {user.fullName?.charAt(0)}
                </Avatar>
              </Tooltip>
            ))}
          </AvatarGroup>
        );
      } else {
        return (
          <Box
            sx={{
              color: "text.secondary",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              height: "100%",
            }}
          >
            <Person fontSize="small" />
            <Typography variant="caption">{t("Unassigned")}</Typography>
          </Box>
        );
      }
    },
    [t]
  );

  const getColumns = useCallback(
    (isEpicTask: boolean): GridColDef[] => [
      {
        field: "dragHandle",
        headerName: "",
        width: 40,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <DraggableHandle id={params.id as string} disabled={false} />
        ),
      },
      {
        field: "taskInfo",
        headerName: t(isEpicTask ? "Linked Task" : "Sub Task"),
        flex: 2,
        minWidth: 250,
        renderCell: (params) => {
          const task = params.row;
          const type = task?.type || "";
          const typeInfo = type
            ? typeToIconMapping[type]
            : typeToIconMapping.TASK;
          const displayText = task?.bugKey
            ? `${task.bugKey}: ${task.summary}`
            : task.summary;

          const subTaskCount = task?.subTasks?.length;

          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                py: 0.5,
                height: "100%",
                cursor: "pointer",
              }}
              onClick={() => onTaskClick(task._id)}
            >
              {type && (
                <Tooltip title={type} placement="top-start">
                  <Box
                    component="span"
                    sx={{
                      width: 14,
                      height: 14,
                      mr: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: typeInfo?.bgcolor,
                      borderRadius: "2px",
                      color: "white",
                      flexShrink: 0,
                      fontSize: "10px",
                    }}
                  >
                    {typeInfo.icon}
                  </Box>
                </Tooltip>
              )}
              <Tooltip title={displayText} placement="top-start">
                <Typography
                  variant="body2"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontWeight: 500,
                    fontSize: "0.8rem",
                  }}
                >
                  {task?.bugKey && (
                    <span style={{ marginRight: "4px", color: "#1976D2" }}>
                      {task.bugKey}:
                    </span>
                  )}
                  {task.summary}
                </Typography>
              </Tooltip>
              {subTaskCount > 0 && (
                <Tooltip title="Sub-tasks">
                  <Typography
                    variant="body2"
                    color="text.primary"
                    sx={{ ml: 1.5 }}
                  >
                    ({subTaskCount})
                  </Typography>
                </Tooltip>
              )}
            </Box>
          );
        },
      },
      {
        field: "assignTo",
        headerName: t("Assignee"),
        width: 120,
        renderCell: (params) => renderAssignee(params.row.assignTo),
      },
      {
        field: "bugStatus",
        headerName: t("Status"),
        width: 100,
        renderCell: (params) => (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {params.row.bugStatus?.status}
          </Typography>
        ),
      },
      {
        field: "actions",
        type: "actions",
        headerName: t("Actions"),
        width: 80,
        getActions: (params) => [
          <GridActionsCellItem
            key="edit"
            icon={<EditIcon fontSize="small" />}
            label="Edit"
            onClick={() => {
              onTaskClick(params.id);
            }}
          />,
        ],
      },
    ],
    [onTaskClick, renderAssignee, t, theme]
  );

  const epicColumns = useMemo(() => getColumns(true), [getColumns]);
  const subTaskColumns = useMemo(() => getColumns(false), [getColumns]);

  if (isLoadingSubTasks) {
    return (
      <Box
        sx={{
          width: "100%",
          p: 2,
          pl: 20,
          backgroundColor: theme.palette.grey[25],
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">
          {t("Loading sub-tasks...")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        p: 2,
        pl: 20,
        backgroundColor: theme.palette.grey[25],
      }}
    >
      {hasEpicTasks && (
        <Box sx={{ mb: hasSubTasks ? 3 : 0 }}>
          <Typography
            variant="subtitle2"
            sx={{ mb: 1, fontWeight: 600, color: theme.palette.primary.main }}
          >
            {t("Linked Tasks")}
          </Typography>
          <Paper sx={{ width: "100%" }}>
            <DataGridPro
              rows={epicTasks}
              columns={epicColumns}
              getRowId={(row) => row?._id}
              rowSelection={false}
              hideFooter={true}
              pagination={false}
              slots={{
                noRowsOverlay: () => (
                  <GridOverlay>
                    <Typography variant="body2" color="text.secondary">
                      {t("No linked tasks found")}
                    </Typography>
                  </GridOverlay>
                ),
              }}
              sx={{
                border: "none",
                "& .MuiDataGrid-cell:focus": {
                  outline: "none",
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
              autoHeight
            />
          </Paper>
        </Box>
      )}

      {hasSubTasks && (
        <Box>
          <Typography
            variant="subtitle2"
            sx={{ mb: 1, fontWeight: 600, color: theme.palette.primary.main }}
          >
            {t("Sub-Tasks")}
          </Typography>
          <Paper sx={{ width: "100%" }}>
            <DataGridPro
              rows={subTasks}
              columns={subTaskColumns}
              getRowId={(row) => row?._id}
              rowSelection={false}
              hideFooter={true}
              pagination={false}
              slots={{
                noRowsOverlay: () => (
                  <GridOverlay>
                    <Typography variant="body2" color="text.secondary">
                      {t("No sub-tasks found")}
                    </Typography>
                  </GridOverlay>
                ),
              }}
              sx={{
                border: "none",
                "& .MuiDataGrid-cell:focus": {
                  outline: "none",
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
              autoHeight
            />
          </Paper>
        </Box>
      )}

      {!hasEpicTasks && !hasSubTasks && (
        <Box
          sx={{
            p: 3,
            textAlign: "center",
            color: theme.palette.text.secondary,
          }}
        >
          <Typography variant="body2">
            {t("No linked tasks or sub-tasks found")}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

/**
 * DroppableTaskContainer Component
 */
interface DroppableTaskContainerProps {
  droppableId: string;
  children: React.ReactNode;
  isExpanded: boolean;
  isDragging?: boolean;
}

const DroppableTaskContainer: React.FC<DroppableTaskContainerProps> = ({
  droppableId,
  children,
  isExpanded,
  isDragging = false,
}) => {
  const { t } = useTranslation(["bugherd"]);
  const theme = useTheme();

  const { isOver, setNodeRef } = useDroppable({
    id: droppableId,
    data: {
      type: "container",
      containerId: droppableId,
    },
  });

  if (!isExpanded) {
    return <>{children}</>;
  }

  return (
    <Box
      ref={setNodeRef}
      sx={{
        minHeight: isDragging ? 60 : "auto",
        transition: "all 0.2s ease",
        backgroundColor: isOver
          ? `${theme.palette.primary.main}15`
          : "transparent",
        border: isOver
          ? `2px dashed ${theme.palette.primary.main}`
          : "2px dashed transparent",
        borderRadius: 1,
        ...(isOver && {
          boxShadow: `0 0 10px ${theme.palette.primary.main}30`,
        }),
      }}
    >
      {children}
      {isOver && isDragging && (
        <Box
          sx={{
            p: 2,
            textAlign: "center",
            color: theme.palette.primary.main,
            fontWeight: 500,
            fontSize: "0.875rem",
            backgroundColor: `${theme.palette.primary.main}08`,
            borderRadius: 1,
            margin: 1,
          }}
        >
          {t("Drop task(s) here")}
        </Box>
      )}
    </Box>
  );
};
interface DroppableEpicRowProps {
  epicId: string;
  children: React.ReactNode;
  isDragging?: boolean;
}

const DroppableEpicRow: React.FC<DroppableEpicRowProps> = ({
  epicId,
  children,
  isDragging = false,
}) => {
  const theme = useTheme();
  const { isOver, setNodeRef } = useDroppable({
    id: `epic-${epicId}`,
    data: {
      type: "epic",
      epicId: epicId,
    },
  });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        position: "relative",
        transition: "all 0.2s ease",
        backgroundColor: isOver
          ? `${theme.palette.success.main}15`
          : "transparent",
        border: isOver
          ? `2px solid ${theme.palette.success.main}`
          : "2px solid transparent",
        borderRadius: isOver ? 1 : 0,
        ...(isOver && {
          boxShadow: `0 0 10px ${theme.palette.success.main}30`,
        }),
      }}
    >
      {children}
      {isOver && isDragging && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: `${theme.palette.success.main}10`,
            borderRadius: 1,
            pointerEvents: "none",
            zIndex: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.success.main,
              fontWeight: 600,
              backgroundColor: theme.palette.background.paper,
              px: 2,
              py: 1,
              borderRadius: 1,
              boxShadow: theme.shadows[2],
            }}
          >
            Link to this EPIC
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const TaskDataGrid: React.FC<TaskDataGridProps> = ({
  accordionKey,
  droppableId,
  title,
  isExpanded,
  onAccordionChange,
  tasks,
  totalCount,
  isLoading,
  paginationModel,
  setPaginationModel,
  onTaskClick,
  onMenuClick,
  isCreatingTask,
  newTaskName,
  onCreateTaskClick,
  onTaskNameChange,
  onCreateTask,
  onCancelTaskCreation,
  onKeyPress,
  isCreatingBug,
  headerButtons,
  sprint,
  onDeleteSprint,
  onEditSprint,
  isDeletingSprint = false,
  emptyMessage,
  isDragging = false,
  selectedTasks,
  onSelectionChange,
  isSelectionDisabled = false,
  onStatusClick,
  editingStatusTaskId,
  statusList,
  isUpdatingStatus = false,
  viewMode = "all",
  onViewModeChange,
  availableUsers = [],
  availableTypes = [],
  onTaskUpdate,
  newTaskType = "STORY",
  onNewTaskTypeChange,
}) => {
  const { t } = useTranslation(["bugherd"]);
  const theme = useTheme();
  const textFieldRef = useRef<HTMLInputElement>(null);

  // Refs for props to stabilize columns
  const onMenuClickRef = useRef(onMenuClick);
  const onTaskClickRef = useRef(onTaskClick);

  const { projectDetails, companyMemberData } = useAppSelector(selectApiData);

  const getProjectFilteredUsers = () => {
    const allCompanyUsers = companyMemberData;

    if (!allCompanyUsers || !Array.isArray(allCompanyUsers)) {
      return [];
    }

    if (!projectDetails?.users) {
      return allCompanyUsers.filter(
        (x: any) => x._id === projectDetails?.owner
      );
    }

    // Extract userIds from project users (using userId field, not _id)
    const projectUserIds = projectDetails.users.map((user: any) => {
      if (typeof user === "object") {
        return user?.userId; // This is correct based on your data structure
      }
      return user;
    });

    const projectOwnerId = projectDetails.owner; // This is the _id, not userId

    // Find the owner's userId from company members
    const ownerUser = allCompanyUsers.find(
      (member: any) => member._id === projectOwnerId
    );
    const ownerUserId = ownerUser?.userId;

    // Combine all userIds (both from project.users and owner)
    const allUserIds =
      ownerUserId && !projectUserIds.includes(ownerUserId)
        ? [...projectUserIds, ownerUserId]
        : projectUserIds;

    // Filter company members by userId
    const result = allCompanyUsers.filter((member: any) =>
      allUserIds.includes(member.userId)
    );

    return result;
  };

  useEffect(() => {
    onMenuClickRef.current = onMenuClick;
  }, [onMenuClick]);

  useEffect(() => {
    onTaskClickRef.current = onTaskClick;
  }, [onTaskClick]);

  // Use position-based anchoring for Sprint Menu
  const [sprintMenuAnchorEl, setSprintMenuAnchorEl] = useState<{
    element: HTMLElement;
    position: { top: number; left: number };
  } | null>(null);

  // Use position-based anchoring for popovers
  const [assigneeAnchorEl, setAssigneeAnchorEl] = useState<{
    element: HTMLElement;
    position: { top: number; left: number };
    taskId: string;
  } | null>(null);

  const [statusAnchorEl, setStatusAnchorEl] = useState<{
    element: HTMLElement;
    position: { top: number; left: number };
    taskId: string;
  } | null>(null);

  const [typeAnchorEl, setTypeAnchorEl] = useState<{
    element: HTMLElement;
    position: { top: number; left: number };
    taskId: string;
  } | null>(null);

  const [createTaskTypeAnchorEl, setCreateTaskTypeAnchorEl] = useState<{
    element: HTMLElement;
    position: { top: number; left: number };
  } | null>(null);

  const [optimisticUpdates, setOptimisticUpdates] = useState<{
    [taskId: string]: Partial<BugItem>;
  }>({});

  const [searchQuery, setSearchQuery] = useState<string>("");

  const getTaskWithOptimisticUpdates = useCallback(
    (task: BugItem): BugItem => {
      const updates = optimisticUpdates[task._id];
      return updates ? { ...task, ...updates } : task;
    },
    [optimisticUpdates]
  );

  const handleMainPaginationChange = (newPaginationModel: {
    page: number;
    pageSize: number;
  }) => {
    setPaginationModel(newPaginationModel);
  };

  useEffect(() => {
    if (isCreatingTask && textFieldRef.current) {
      textFieldRef.current.focus();
    }
  }, [isCreatingTask]);

  const handleSprintMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setSprintMenuAnchorEl({
      element: event.currentTarget,
      position: { top: rect.bottom, left: rect.left },
    });
  };

  const handleSprintMenuClose = () => {
    setSprintMenuAnchorEl(null);
  };

  const handleDeleteSprintClick = () => {
    if (sprint && onDeleteSprint) {
      onDeleteSprint(sprint._id);
    }
    handleSprintMenuClose();
  };

  const handleEditSprintClick = () => {
    if (sprint && onEditSprint) {
      onEditSprint(sprint._id);
    }
    handleSprintMenuClose();
  };

  const handleRowSelectionChange = (newSelection: GridRowId[]) => {
    const newSelectionSet = new Set(newSelection.map((id) => String(id)));
    onSelectionChange(newSelectionSet);
  };

  // Check if row has detail panel content
  const hasDetailPanel = (row: BugItem) => {
    const hasEpicTasks = (row.epicTasks?.length ?? 0) > 0;
    const hasSubTasks =
      (row.subTasks?.length ?? 0) > 0 || (row.subTasksCount ?? 0) > 0;
    return hasEpicTasks || hasSubTasks;
  };

  // Assignee click handler
  const handleAssigneeClick = useCallback(
    (event: React.MouseEvent<HTMLElement>, taskId: string) => {
      event.stopPropagation();
      const rect = event.currentTarget.getBoundingClientRect();
      setAssigneeAnchorEl({
        element: event.currentTarget,
        position: { top: rect.bottom, left: rect.left },
        taskId,
      });
    },
    []
  );

  const handleAssigneeClose = () => {
    setAssigneeAnchorEl(null);
  };

  const handleStatusClick = useCallback(
    (event: React.MouseEvent<HTMLElement>, taskId: string) => {
      event.stopPropagation();
      const rect = event.currentTarget.getBoundingClientRect();
      setStatusAnchorEl({
        element: event.currentTarget,
        position: { top: rect.bottom, left: rect.left },
        taskId,
      });
    },
    []
  );

  const handleStatusClose = () => {
    setStatusAnchorEl(null);
  };

  const handleStatusSelect = async (taskId: string, newStatus: any) => {
    if (!onTaskUpdate) return;

    const task = tasks.find((t) => t._id === taskId);
    if (!task) return;

    setOptimisticUpdates((prev) => ({
      ...prev,
      [taskId]: {
        ...prev[taskId],
        bugStatus: newStatus,
      },
    }));

    handleStatusClose();

    const updatedTask = {
      ...task,
      bugStatus: newStatus,
    };

    try {
      await onTaskUpdate(taskId, updatedTask, "bugStatus");
      setOptimisticUpdates((prev) => {
        const { [taskId]: _, ...rest } = prev;
        return rest;
      });
    } catch (error) {
      setOptimisticUpdates((prev) => {
        const { [taskId]: _, ...rest } = prev;
        return rest;
      });
      console.error("Failed to update status:", error);
    }
  };

  const handleAssigneeToggle = async (taskId: string, user: any) => {
    const task = tasks.find((t) => t._id === taskId);
    if (!task || !onTaskUpdate) return;

    const currentTask = getTaskWithOptimisticUpdates(task);
    const currentAssignees = currentTask.assignTo || [];
    const isAssigned = currentAssignees.some((u) => u._id === user._id);

    const newAssignees = isAssigned
      ? currentAssignees.filter((u) => u._id !== user._id)
      : [
          ...currentAssignees,
          {
            _id: user._id,
            fullName: user.fullName,
          },
        ];

    // Update optimistic state immediately
    setOptimisticUpdates((prev) => ({
      ...prev,
      [taskId]: { ...prev[taskId], assignTo: newAssignees },
    }));

    const updatedTask = { ...task, assignTo: newAssignees };

    try {
      setAssigneeAnchorEl(null);
      await onTaskUpdate(taskId, updatedTask, "assignTo");
      // Clear optimistic update on success
      // setOptimisticUpdates((prev) => {
      //   const { [taskId]: _, ...rest } = prev;
      //   return rest;
      // });
    } catch (error) {
      setAssigneeAnchorEl(null);
      // Revert optimistic update on error
      // setOptimisticUpdates((prev) => {
      //   const { [taskId]: _, ...rest } = prev;
      //   return rest;
      // });
      console.error("Failed to update assignee:", error);
    }
  };

  // Type click handler
  const handleTypeClick = useCallback(
    (event: React.MouseEvent<HTMLElement>, taskId: string) => {
      event.stopPropagation();
      const rect = event.currentTarget.getBoundingClientRect();
      setTypeAnchorEl({
        element: event.currentTarget,
        position: { top: rect.bottom, left: rect.left },
        taskId,
      });
    },
    []
  );

  const handleTypeClose = () => {
    setTypeAnchorEl(null);
  };

  const handleTypeSelect = async (taskId: string, typeCode: string) => {
    if (!onTaskUpdate) return;

    const task = tasks.find((t) => t._id === taskId);
    if (!task) return;

    // Update optimistic state immediately
    setOptimisticUpdates((prev) => ({
      ...prev,
      [taskId]: { ...prev[taskId], type: typeCode },
    }));

    handleTypeClose();

    const updatedTask = { ...task, type: typeCode };

    try {
      await onTaskUpdate(taskId, updatedTask, "type");
      // Clear optimistic update on success
      setOptimisticUpdates((prev) => {
        const { [taskId]: _, ...rest } = prev;
        return rest;
      });
    } catch (error) {
      // Revert optimistic update on error
      setOptimisticUpdates((prev) => {
        const { [taskId]: _, ...rest } = prev;
        return rest;
      });
      console.error("Failed to update type:", error);
    }
  };

  // ✅ NEW: Handler for create task type icon click
  const handleCreateTaskTypeClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setCreateTaskTypeAnchorEl({
      element: event.currentTarget,
      position: { top: rect.bottom, left: rect.left },
    });
  };

  const handleCreateTaskTypeClose = () => {
    setCreateTaskTypeAnchorEl(null);
  };

  const handleCreateTaskTypeSelect = (typeCode: string) => {
    if (onNewTaskTypeChange) {
      onNewTaskTypeChange(typeCode);
    }
    handleCreateTaskTypeClose();
  };

  // ✅ NEW: Click away handler for create task row
  const handleCreateTaskClickAway = (event: MouseEvent | TouchEvent) => {
    // Don't close if clicking on the type popover
    const target = event.target as HTMLElement;
    if (
      createTaskTypeAnchorEl &&
      createTaskTypeAnchorEl.element.contains(target)
    ) {
      return;
    }

    // Check if clicking inside any popover
    if (target.closest(".MuiPopover-root")) {
      return;
    }

    onCancelTaskCreation();
  };

  const renderAssignee = useCallback(
    (assignees: any[], taskId: string) => {
      const task = tasks.find((t) => t._id === taskId);
      const optimisticTask = task ? getTaskWithOptimisticUpdates(task) : null;
      const displayAssignees = optimisticTask?.assignTo || assignees;

      if (displayAssignees && displayAssignees.length > 0) {
        return (
          <Box
            onClick={(e) => handleAssigneeClick(e, taskId)}
            sx={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              height: "100%",
              "&:hover": {
                opacity: 0.8,
              },
            }}
          >
            <AvatarGroup
              max={3}
              spacing="medium"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                height: "100%",
                "& .MuiAvatar-root": {
                  width: 24,
                  height: 24,
                  fontSize: 12,
                },
              }}
            >
              {displayAssignees.map((user, index) => (
                <Tooltip key={user._id} title={user.fullName}>
                  <Avatar
                    sx={{ bgcolor: colorPalette[index % colorPalette.length] }}
                  >
                    {user.fullName?.charAt(0)}
                  </Avatar>
                </Tooltip>
              ))}
            </AvatarGroup>
          </Box>
        );
      } else {
        return (
          <Box
            onClick={(e) => handleAssigneeClick(e, taskId)}
            sx={{
              color: "text.secondary",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              height: "100%",
              cursor: "pointer",
              "&:hover": {
                color: "primary.main",
              },
            }}
          >
            <Person fontSize="small" />
            <Typography variant="body2">{t("Unassigned")}</Typography>
          </Box>
        );
      }
    },
    [tasks, getTaskWithOptimisticUpdates, t, handleAssigneeClick]
  );

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: "dragHandle",
        headerName: "",
        width: 40,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const isSelected = selectedTasks.has(params.id as string);
          return (
            <DraggableHandle id={params.id as string} disabled={!isSelected} />
          );
        },
      },
      {
        field: "taskInfo",
        headerName: t("Work Item"),
        flex: 2,
        minWidth: 180,
        renderCell: (params) => {
          const task = params.row;
          const optimisticTask = getTaskWithOptimisticUpdates(task);
          const type = optimisticTask.type || "";
          const typeInfo = type
            ? typeToIconMapping[type]
            : typeToIconMapping.TASK;
          const displayText = task?.bugKey
            ? `${task.bugKey}: ${task.summary}`
            : task.summary;

          const epicTaskCount = task?.epicTasks?.length || 0;
          const subTaskCount = epicTaskCount
            ? 0
            : task?.subTasksCount || task?.subTasks?.length || 0;

          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                py: 1,
                cursor: "pointer",
                height: "100%",
                gap: 1,
              }}
            >
              {type && (
                <Tooltip title={type} placement="top-start">
                  <Box
                    component="span"
                    onClick={(e) => handleTypeClick(e, task._id)}
                    sx={{
                      width: 16,
                      height: 16,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: typeInfo?.bgcolor,
                      borderRadius: "3px",
                      color: "white",
                      flexShrink: 0,
                      cursor: "pointer",
                      "&:hover": {
                        opacity: 0.8,
                        transform: "scale(1.1)",
                      },
                      transition: "all 0.2s ease",
                    }}
                  >
                    {typeInfo.icon}
                  </Box>
                </Tooltip>
              )}
              <Tooltip title={displayText} placement="top-start">
                <Typography
                  variant="body2"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontWeight: 500,
                  }}
                  onClick={() => onTaskClickRef.current(task._id)}
                >
                  {task?.bugKey && (
                    <span style={{ marginRight: "4px", color: "#1976D2" }}>
                      {task.bugKey}:
                    </span>
                  )}
                  {task.summary}
                </Typography>
              </Tooltip>
              {task.epic && (
                <Chip
                  label={`${task?.epic?.bugKey || task?.epic?.summary}`}
                  size="small"
                  icon={<BoltIcon sx={{ fontSize: "14px !important" }} />}
                  variant="outlined"
                  sx={{
                    height: 20,
                    fontSize: "0.7rem",
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                    backgroundColor: `${theme.palette.primary.main}08`,
                    fontWeight: 500,
                  }}
                />
              )}
              {epicTaskCount > 0 && (
                <Tooltip title="Linked tasks">
                  <Chip
                    label={epicTaskCount}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: "0.7rem",
                      backgroundColor: theme.palette.primary.light,
                      color: theme.palette.primary.contrastText,
                    }}
                  />
                </Tooltip>
              )}
              {subTaskCount > 0 && (
                <Tooltip title="Sub-tasks">
                  <Chip
                    label={subTaskCount}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: "0.7rem",
                      backgroundColor: theme.palette.primary.light,
                      color: theme.palette.primary.contrastText,
                    }}
                  />
                </Tooltip>
              )}
            </Box>
          );
        },
      },
      {
        field: "assignTo",
        headerName: t("Assignee"),
        width: 200,
        renderCell: (params) => renderAssignee(params.value, params.row._id),
      },
      {
        field: "bugStatus",
        headerName: t("Status"),
        width: 120,
        renderCell: (params) => {
          const task = params.row;
          const optimisticTask = getTaskWithOptimisticUpdates(task);
          const status = optimisticTask.bugStatus;
          const color = statusList?.find((x) => x._id === status?._id)?.color;
          return (
            <Box
              onClick={(e) => handleStatusClick(e, task._id)}
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                cursor: "pointer",
                gap: 1,
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "2px",
                  backgroundColor: color || theme.palette.grey[400],
                  flexShrink: 0,
                  transition: "background-color 0.2s ease",
                }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: 1,
                  fontWeight: 500,
                }}
              >
                {status?.status}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: "actions",
        type: "actions",
        headerName: t("Actions"),
        width: 80,
        getActions: (params) => [
          <GridActionsCellItem
            key="more"
            icon={<MoreVertIcon fontSize="small" />}
            label="More"
            onClick={(event) => {
              event.stopPropagation();
              onMenuClickRef.current(
                event as React.MouseEvent<HTMLElement>,
                params.id as string
              );
            }}
          />,
        ],
      },
    ],
    [
      t,
      theme,
      selectedTasks,
      renderAssignee,
      handleStatusClick,
      handleTypeClick,
      getTaskWithOptimisticUpdates,
      statusList,
    ]
  );

  const rows = useMemo(() => {
    return tasks.map((task) => ({
      id: task._id,
      ...task,
    }));
  }, [tasks]);

  const getDetailPanelContent = ({ row }: { row: BugItem }) => {
    return (
      <SubTasksDetailPanel
        parentTaskId={row._id}
        parentTask={row}
        onTaskClick={onTaskClick}
      />
    );
  };

  const getDetailPanelHeight = () => "auto";
  const renderCreateTaskRow = () => {
    const selectedTypeInfo =
      typeToIconMapping[newTaskType] || typeToIconMapping.STORY;

    return (
      <ClickAwayListener onClickAway={handleCreateTaskClickAway}>
        <Box
          key={`create-task-${newTaskType}`}
          sx={{
            display: "flex",
            alignItems: "center",
            px: 2,
            py: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
            minHeight: 52,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Box sx={{ width: 24, mr: 1 }} />
          <Box sx={{ width: 32 }} />
          <Tooltip title="Click to change type">
            <Box
              component="span"
              onClick={handleCreateTaskTypeClick}
              sx={{
                width: 16,
                height: 16,
                mr: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: selectedTypeInfo.bgcolor,
                borderRadius: "3px",
                color: "white",
                flexShrink: 0,
                cursor: "pointer",
                "&:hover": {
                  opacity: 0.8,
                  transform: "scale(1.1)",
                },
                transition: "all 0.2s ease",
              }}
            >
              {selectedTypeInfo.icon}
            </Box>
          </Tooltip>
          <TextField
            ref={textFieldRef}
            value={newTaskName}
            onChange={(e) => onTaskNameChange(e.target.value)}
            onKeyDown={onKeyPress}
            placeholder={t("Enter task name and press Enter")}
            variant="standard"
            size="small"
            disabled={isCreatingBug}
            autoFocus
            inputProps={{
              "data-testid": `create-task-${accordionKey}`,
            }}
            sx={{
              flex: 1,
              "& .MuiInput-root": {
                "&:before": {
                  borderBottom: "1px solid transparent",
                },
                "&:after": {
                  borderBottom: `2px solid ${theme.palette.primary.main}`,
                },
              },
            }}
            InputProps={{
              disableUnderline: false,
              endAdornment: isCreatingBug && (
                <InputAdornment position="end">
                  <Typography variant="caption" color="text.secondary">
                    {t("Creating...")}
                  </Typography>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </ClickAwayListener>
    );
  };

  const formatSprintDateRange = (startDate?: string, endDate?: string) => {
    if (!startDate || !endDate) return null;

    const start = new Date(startDate);
    const end = new Date(endDate);

    const startDay = start.getDate();
    const startMonth = start.toLocaleDateString("en-US", { month: "short" });
    const endDay = end.getDate();
    const endMonth = end.toLocaleDateString("en-US", { month: "short" });

    if (start.getMonth() === end.getMonth()) {
      return `${startDay} - ${endDay} ${endMonth}`;
    } else {
      return `${startDay} ${startMonth} - ${endDay} ${endMonth}`;
    }
  };

  // Get current task with optimistic updates for popovers
  const currentTaskForAssignee = assigneeAnchorEl
    ? getTaskWithOptimisticUpdates(
        tasks.find((t) => t._id === assigneeAnchorEl.taskId)!
      )
    : null;
  const currentTaskForType = typeAnchorEl
    ? getTaskWithOptimisticUpdates(
        tasks.find((t) => t._id === typeAnchorEl.taskId)!
      )
    : null;
  const currentTaskForStatus = statusAnchorEl
    ? getTaskWithOptimisticUpdates(
        tasks.find((t) => t._id === statusAnchorEl.taskId)!
      )
    : null;

  return (
    <Paper sx={{ mb: 2, overflow: "hidden" }}>
      <Accordion
        expanded={isExpanded}
        onChange={onAccordionChange}
        disableGutters
        elevation={0}
        sx={{
          "&:before": {
            display: "none",
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            backgroundColor: theme.palette.grey[50],
            borderBottom: `1px solid ${theme.palette.divider}`,
            minHeight: 48,
            "& .MuiAccordionSummary-content": {
              margin: "8px 0",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="subtitle1"
              fontWeight={600}
              color="text.secondary"
            >
              {title}
            </Typography>
            {isExpanded && (
              <Typography variant="body2" color="text.secondary">
                ({totalCount})
              </Typography>
            )}
            {sprint && (
              <>
                <Chip
                  label={sprint.status}
                  size="small"
                  color={sprint.status === "ACTIVE" ? "primary" : "default"}
                  sx={{
                    fontWeight: sprint.status === "ACTIVE" ? "bold" : "normal",
                  }}
                  variant={sprint.status === "ACTIVE" ? "filled" : "outlined"}
                />
                {sprint.startDate && sprint.endDate ? (
                  <Chip
                    label={formatSprintDateRange(
                      sprint.startDate,
                      sprint.endDate
                    )}
                    size="small"
                    variant="outlined"
                    sx={{
                      backgroundColor: theme.palette.background.paper,
                      borderColor: theme.palette.divider,
                    }}
                  />
                ) : (
                  <Button
                    size="small"
                    variant="text"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onEditSprint) {
                        onEditSprint(sprint._id);
                      }
                    }}
                    sx={{
                      minWidth: "auto",
                      px: 1,
                      py: 0.25,
                      fontSize: "0.75rem",
                      textTransform: "none",
                      color: theme.palette.primary.main,
                      "&:hover": {
                        backgroundColor: `${theme.palette.primary.main}08`,
                      },
                    }}
                  >
                    {t("Add dates")}
                  </Button>
                )}
              </>
            )}
            {selectedTasks.size > 0 && (
              <Chip
                label={`${selectedTasks.size} selected`}
                size="small"
                sx={{ fontWeight: "bold" }}
                color="primary"
                variant="outlined"
              />
            )}

            {onViewModeChange && isExpanded && (
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(event, newMode) => {
                  event.stopPropagation();
                  if (onViewModeChange) {
                    onViewModeChange(event, newMode);
                  }
                }}
                size="small"
                sx={{
                  ml: 2,
                  "& .MuiToggleButton-root": {
                    px: 1.5,
                    py: 0.3,
                    fontSize: "0.75rem",
                    textTransform: "none",
                    border: `1px solid ${theme.palette.divider}`,
                    "&.Mui-selected": {
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      "&:hover": {
                        backgroundColor: theme.palette.primary.dark,
                      },
                    },
                  },
                }}
              >
                <ToggleButton value="all">
                  <ViewAgendaIcon sx={{ fontSize: 14, mr: 0.5 }} />
                  {t("All")}
                </ToggleButton>
                <ToggleButton value="epics">
                  <AccountTreeIcon sx={{ fontSize: 14, mr: 0.5 }} />
                  {t("Epics")}
                </ToggleButton>
                <ToggleButton value="stories">
                  <ViewListIcon sx={{ fontSize: 14, mr: 0.5 }} />
                  {t("Stories/Tasks")}
                </ToggleButton>
              </ToggleButtonGroup>
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {headerButtons &&
              headerButtons.map((button, index) => (
                <Button
                  key={index}
                  variant="outlined"
                  size="small"
                  startIcon={button.icon}
                  onClick={(e) => {
                    e.stopPropagation();
                    button.onClick(e);
                  }}
                  disabled={button.disabled}
                  sx={{
                    textTransform: "none",
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                    "&:hover": {
                      borderColor: theme.palette.primary.dark,
                      backgroundColor: `${theme.palette.primary.main}08`,
                    },
                  }}
                >
                  {button.label}
                </Button>
              ))}
            {sprint && onDeleteSprint && (
              <IconButton
                size="small"
                onClick={handleSprintMenuClick}
                disabled={isDeletingSprint}
                sx={{
                  color: theme.palette.text.secondary,
                  "&:hover": {
                    color: theme.palette.text.primary,
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </AccordionSummary>

        <AccordionDetails sx={{ p: 0 }}>
          <DroppableTaskContainer
            droppableId={droppableId}
            isExpanded={isExpanded}
            isDragging={isDragging}
          >
            {isCreatingTask && renderCreateTaskRow()}

            <Box
              sx={{
                width: "100%",
              }}
            >
              <DataGridPro
                rows={rows}
                autoHeight
                columns={columns}
                loading={isLoading}
                checkboxSelection
                disableRowSelectionOnClick
                rowSelectionModel={Array.from(selectedTasks)}
                onRowSelectionModelChange={handleRowSelectionChange}
                hideFooterSelectedRowCount={false}
                hideFooter={false}
                pagination={true}
                getDetailPanelContent={getDetailPanelContent}
                getDetailPanelHeight={getDetailPanelHeight}
                isRowExpandable={(params: GridRowParams) =>
                  hasDetailPanel(params.row as BugItem)
                }
                isRowSelectable={(params: GridRowParams) => {
                  const task = params.row as BugItem;
                  return task.type !== "EPIC";
                }}
                slots={{
                  row: (props) => {
                    const task = props.row as BugItem;
                    const isEpic = task.type === "EPIC";

                    if (isEpic) {
                      return (
                        <DroppableEpicRow
                          epicId={task._id}
                          isDragging={isDragging}
                        >
                          <GridRow {...props} />
                        </DroppableEpicRow>
                      );
                    }

                    return <GridRow {...props} />;
                  },
                  noRowsOverlay: () => (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                        flexDirection: "column",
                        gap: 1,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {emptyMessage || t("No work items found")}
                      </Typography>
                    </Box>
                  ),
                  detailPanelExpandIcon: KeyboardArrowRightIcon,
                  detailPanelCollapseIcon: KeyboardArrowDownIcon,
                  pagination: () => (
                    <Pagination
                      total={totalCount}
                      paginationModel={paginationModel}
                      setPaginationModel={handleMainPaginationChange}
                    />
                  ),
                }}
                sx={{
                  border: "none",
                  height: "100%",
                  "& .MuiDataGrid-detailPanel": {
                    backgroundColor: theme.palette.grey[25],
                  },
                  "& .MuiDataGrid-cell:focus": {
                    outline: "none",
                  },
                  "& .MuiDataGrid-cell:focus-within": {
                    outline: "none",
                  },
                }}
              />
            </Box>
          </DroppableTaskContainer>
          {isExpanded && (
            <Box
              sx={{
                borderTop: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.grey[25],
                p: 1,
              }}
            >
              {isCreatingTask ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ pl: 1 }}
                >
                  {t("Press Enter to create • ESC to cancel")}
                </Typography>
              ) : (
                accordionKey !== "backlog" && (
                  <Button
                    variant="text"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={onCreateTaskClick}
                    disabled={isCreatingBug}
                    sx={{
                      textTransform: "none",
                      color: theme.palette.text.secondary,
                      "&:hover": {
                        color: theme.palette.primary.main,
                        backgroundColor: `${theme.palette.primary.main}08`,
                      },
                    }}
                  >
                    {t("Create")}
                  </Button>
                )
              )}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Sprint Menu */}
      <Menu
        anchorEl={sprintMenuAnchorEl?.element}
        anchorReference="anchorPosition"
        anchorPosition={sprintMenuAnchorEl?.position}
        open={Boolean(sprintMenuAnchorEl)}
        onClose={handleSprintMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={handleEditSprintClick} disabled={isDeletingSprint}>
          <EditIcon sx={{ mr: 1, fontSize: 20 }} />
          <Typography variant="body2">{t("Edit Sprint")}</Typography>
        </MenuItem>
        <MenuItem
          onClick={handleDeleteSprintClick}
          disabled={isDeletingSprint}
          sx={{
            color: theme.palette.error.main,
            "&:hover": {
              backgroundColor: theme.palette.error.light + "20",
            },
          }}
        >
          <DeleteIcon sx={{ mr: 1, fontSize: 20 }} />
          <Typography variant="body2">
            {isDeletingSprint ? t("Deleting...") : t("Delete Sprint")}
          </Typography>
        </MenuItem>
      </Menu>

      {/* Assignee Popover - UPDATED */}
      <Popover
        open={Boolean(assigneeAnchorEl)}
        anchorEl={assigneeAnchorEl?.element}
        anchorReference="anchorPosition"
        anchorPosition={assigneeAnchorEl?.position}
        onClose={() => {
          handleAssigneeClose();
          setSearchQuery(""); // Clear search when closing
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            width: 300,
            maxHeight: 400,
            p: 1,
          },
        }}
      >
        <Box sx={{ p: 1, mb: 1 }}>
          {/* Search Box */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              background: "#f5f5f5",
              borderRadius: 1,
              p: 0.5,
              mb: 1,
            }}
          >
            <Search sx={{ color: "text.secondary", ml: 1, mr: 1 }} />
            <InputBase
              placeholder={t("Search people...")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ ml: 1, flex: 1 }}
              onClick={(e) => e.stopPropagation()}
            />
            {searchQuery && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setSearchQuery("");
                }}
              >
                <Close fontSize="small" />
              </IconButton>
            )}
          </Box>

          {/* Assigned Users Section */}
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            {t("Assigned")}
          </Typography>

          {currentTaskForAssignee?.assignTo &&
          currentTaskForAssignee.assignTo.length > 0 ? (
            currentTaskForAssignee.assignTo
              .filter((user: any) =>
                user.fullName.toLowerCase().includes(searchQuery.toLowerCase())
              )
              .map((user: any, index: number) => (
                <MenuItem
                  key={user._id}
                  onClick={() =>
                    assigneeAnchorEl &&
                    handleAssigneeToggle(assigneeAnchorEl.taskId, user)
                  }
                  sx={{ borderRadius: 1, mb: 0.5 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 24,
                        height: 24,
                        mr: 1,
                        background:
                          colorPalette[
                            currentTaskForAssignee.assignTo.findIndex(
                              (u: any) => u._id === user._id
                            ) % colorPalette.length
                          ],
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontSize: "11px" }}>
                        {user.fullName?.charAt(0)}
                      </Typography>
                    </Avatar>
                    <Typography sx={{ flexGrow: 1, fontSize: "14px" }}>
                      {user.fullName}
                    </Typography>
                    <Checkbox checked={true} size="small" />
                  </Box>
                </MenuItem>
              ))
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ p: 1, fontSize: "12px", fontStyle: "italic" }}
            >
              {t("No users assigned")}
            </Typography>
          )}

          <Divider sx={{ my: 1 }} />

          {/* Suggestions Section - Using getProjectFilteredUsers() */}
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            {t("Suggestions")}
          </Typography>

          {getProjectFilteredUsers()
            ?.filter(
              (user: any) =>
                user.fullName
                  .toLowerCase()
                  .includes(searchQuery.toLowerCase()) &&
                !currentTaskForAssignee?.assignTo?.some(
                  (u: any) => u._id === user._id
                )
            )
            .map((user: any, index: number) => (
              <MenuItem
                key={user._id}
                onClick={() =>
                  assigneeAnchorEl &&
                  handleAssigneeToggle(assigneeAnchorEl.taskId, user)
                }
                sx={{ borderRadius: 1, mb: 0.5 }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", width: "100%" }}
                >
                  <Avatar
                    sx={{
                      width: 24,
                      height: 24,
                      mr: 1,
                      background: colorPalette[index % colorPalette.length],
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontSize: "11px" }}>
                      {user.fullName?.charAt(0)}
                    </Typography>
                  </Avatar>
                  <Typography sx={{ flexGrow: 1, fontSize: "14px" }}>
                    {user.fullName}
                  </Typography>
                  <Checkbox checked={false} size="small" />
                </Box>
              </MenuItem>
            ))}

          {getProjectFilteredUsers()?.filter(
            (user: any) =>
              user.fullName.toLowerCase().includes(searchQuery.toLowerCase()) &&
              !currentTaskForAssignee?.assignTo?.some(
                (u: any) => u._id === user._id
              )
          ).length === 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ p: 1, fontSize: "12px", fontStyle: "italic" }}
            >
              {searchQuery
                ? t("No users found")
                : t("No additional users available")}
            </Typography>
          )}
        </Box>
      </Popover>

      <Popover
        open={Boolean(typeAnchorEl)}
        anchorEl={typeAnchorEl?.element}
        anchorReference="anchorPosition"
        anchorPosition={typeAnchorEl?.position}
        onClose={handleTypeClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
          },
        }}
      >
        <Box sx={{ p: 1 }}>
          <Typography
            variant="subtitle2"
            sx={{ px: 2, py: 1, fontWeight: 600 }}
          >
            {t("Change Type")}
          </Typography>
          <Divider />
          <List dense>
            {availableTypes.map((type) => {
              const typeCode = type.code || type.value;
              const typeLabel = type.label;
              const typeInfo = typeToIconMapping[typeCode];
              const isSelected = currentTaskForType?.type === typeCode;

              return (
                <ListItem
                  key={typeCode}
                  button
                  selected={isSelected}
                  onClick={() =>
                    typeAnchorEl &&
                    handleTypeSelect(typeAnchorEl.taskId, typeCode)
                  }
                  sx={{
                    "&:hover": {
                      backgroundColor: theme.palette.action.hover,
                    },
                    "&.Mui-selected": {
                      backgroundColor: `${theme.palette.primary.main}15`,
                      "&:hover": {
                        backgroundColor: `${theme.palette.primary.main}25`,
                      },
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      width: "100%",
                    }}
                  >
                    {typeInfo && (
                      <Box
                        component="span"
                        sx={{
                          width: 20,
                          height: 20,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: typeInfo.bgcolor,
                          borderRadius: "3px",
                          color: "white",
                          flexShrink: 0,
                          fontSize: "12px",
                        }}
                      >
                        {typeInfo.icon}
                      </Box>
                    )}
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: isSelected ? 600 : 400 }}
                    >
                      {typeLabel}
                    </Typography>
                    {isSelected && (
                      <Check
                        sx={{
                          ml: "auto",
                          fontSize: 18,
                          color: theme.palette.primary.main,
                        }}
                      />
                    )}
                  </Box>
                </ListItem>
              );
            })}
          </List>
        </Box>
      </Popover>
      {/* Status Popover */}
      <Popover
        open={Boolean(statusAnchorEl)}
        anchorEl={statusAnchorEl?.element}
        anchorReference="anchorPosition"
        anchorPosition={statusAnchorEl?.position}
        onClose={handleStatusClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
            maxHeight: 350,
          },
        }}
      >
        <Box sx={{ p: 1 }}>
          <Typography
            variant="subtitle2"
            sx={{ px: 2, py: 1, fontWeight: 600 }}
          >
            {t("Change Status")}
          </Typography>
          <Divider />
          <List dense>
            {statusList && statusList.length > 0 ? (
              statusList.map((status: any) => {
                const isSelected =
                  currentTaskForStatus?.bugStatus?._id === status._id;

                return (
                  <ListItem
                    key={status._id}
                    button
                    selected={isSelected}
                    onClick={() =>
                      statusAnchorEl &&
                      handleStatusSelect(statusAnchorEl.taskId, status)
                    }
                    disabled={isUpdatingStatus}
                    sx={{
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                      },
                      "&.Mui-selected": {
                        backgroundColor: `${theme.palette.primary.main}15`,
                        "&:hover": {
                          backgroundColor: `${theme.palette.primary.main}25`,
                        },
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        width: "100%",
                      }}
                    >
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: "2px",
                          backgroundColor:
                            status.color || theme.palette.grey[400],
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          flex: 1,
                          fontWeight: isSelected ? 600 : 400,
                        }}
                      >
                        {status.status}
                      </Typography>
                      {isSelected && (
                        <Check
                          sx={{
                            fontSize: 18,
                            color: theme.palette.primary.main,
                          }}
                        />
                      )}
                    </Box>
                  </ListItem>
                );
              })
            ) : (
              <Box sx={{ p: 2, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  {t("No statuses available")}
                </Typography>
              </Box>
            )}
          </List>
        </Box>
      </Popover>

      <Popover
        open={Boolean(createTaskTypeAnchorEl)}
        anchorEl={createTaskTypeAnchorEl?.element}
        anchorReference="anchorPosition"
        anchorPosition={createTaskTypeAnchorEl?.position}
        onClose={handleCreateTaskTypeClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
          },
        }}
      >
        <Box sx={{ p: 1 }}>
          <Typography
            variant="subtitle2"
            sx={{ px: 2, py: 1, fontWeight: 600 }}
          >
            {t("Select Type")}
          </Typography>
          <Divider />
          <List dense>
            {availableTypes.map((type) => {
              const typeCode = type.code || type.value;
              const typeLabel = type.label;
              const typeInfo = typeToIconMapping[typeCode];
              const isSelected = newTaskType === typeCode;

              return (
                <ListItem
                  key={typeCode}
                  button
                  selected={isSelected}
                  onClick={() => handleCreateTaskTypeSelect(typeCode)}
                  sx={{
                    "&:hover": {
                      backgroundColor: theme.palette.action.hover,
                    },
                    "&.Mui-selected": {
                      backgroundColor: `${theme.palette.primary.main}15`,
                      "&:hover": {
                        backgroundColor: `${theme.palette.primary.main}25`,
                      },
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      width: "100%",
                    }}
                  >
                    {typeInfo && (
                      <Box
                        component="span"
                        sx={{
                          width: 20,
                          height: 20,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: typeInfo.bgcolor,
                          borderRadius: "3px",
                          color: "white",
                          flexShrink: 0,
                          fontSize: "12px",
                        }}
                      >
                        {typeInfo.icon}
                      </Box>
                    )}
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: isSelected ? 600 : 400 }}
                    >
                      {typeLabel}
                    </Typography>
                    {isSelected && (
                      <Check
                        sx={{
                          ml: "auto",
                          fontSize: 18,
                          color: theme.palette.primary.main,
                        }}
                      />
                    )}
                  </Box>
                </ListItem>
              );
            })}
          </List>
        </Box>
      </Popover>
    </Paper>
  );
};

export default TaskDataGrid;
